import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useAuthStore } from "./store";
import * as api from "./api";
import { SessionExpiredError } from "./api";
import type { User, Organization, Brand } from "./types";

// How often to check that the session is still valid (5 minutes)
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  organizations: Organization[];
  currentOrganization: Organization | null;
  currentBrand: Brand | null;
  brands: Brand[];
  login: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  setCurrentOrganization: (org: Organization | null) => void;
  setCurrentBrand: (brand: Brand | null) => void;
  setBrands: (brands: Brand[]) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const {
    user,
    accessToken,
    currentOrganization,
    currentBrand,
    organizations,
    brands,
    setUser,
    setTokens,
    setCurrentOrganization: storeSetCurrentOrg,
    setCurrentBrand: storeSetCurrentBrand,
    setOrganizations: storeSetOrganizations,
    setBrands: storeSetBrands,
    logout: storeLogout,
  } = useAuthStore();

  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(() =>
    useAuthStore.persist?.hasHydrated?.() ?? true,
  );

  // Wait for zustand persist to rehydrate from localStorage before
  // attempting to validate tokens. Without this, AuthProvider runs
  // refreshUser with accessToken=null (initial store state) and sets
  // hasInitialized=true. When rehydration later fills in stale tokens,
  // the heartbeat starts with unvalidated tokens.
  useEffect(() => {
    if (hasHydrated) return;
    if (!useAuthStore.persist?.onFinishHydration) {
      setHasHydrated(true);
      return;
    }
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      setHasHydrated(true);
    });
    // In case hydration already finished between our initial check and
    // subscribing, re-check:
    if (useAuthStore.persist.hasHydrated()) {
      setHasHydrated(true);
    }
    return unsub;
  }, [hasHydrated]);

  // Load user on mount if we have a token
  const refreshUser = useCallback(async () => {
    const tokenAtStart = accessToken;

    if (!tokenAtStart) {
      setIsLoading(false);
      setHasInitialized(true);
      return;
    }

    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
      storeSetOrganizations(userData.organizations || []);

      // Update currentOrganization with fresh data (e.g. enforce2fa changes)
      // Also clear stale currentOrganization if the user is no longer in that org
      const currentOrg = useAuthStore.getState().currentOrganization;
      if (currentOrg) {
        const updated = userData.organizations?.find(
          (o: Organization) => o.id === currentOrg.id,
        );
        if (updated) {
          storeSetCurrentOrg(updated);
        } else {
          // User is no longer a member of this org — clear stale value
          storeSetCurrentOrg(userData.organizations?.[0] ?? null);
        }
      } else if (userData.organizations?.length > 0) {
        storeSetCurrentOrg(userData.organizations[0] ?? null);
      }
    } catch (error) {
      console.error("Failed to load user:", error);
      // Only logout if the session is truly expired (401 → refresh failed).
      // For transient errors (network, 500, etc.) keep the user logged in
      // so they aren't unexpectedly kicked out.
      if (error instanceof SessionExpiredError) {
        // Only logout if the tokens haven't been replaced by a concurrent
        // login (e.g. OTP verify). Without this check, a stale refreshUser
        // finishing after a new login would wipe the fresh session.
        if (useAuthStore.getState().accessToken === tokenAtStart) {
          storeLogout();
        }
      }
    } finally {
      setIsLoading(false);
      setHasInitialized(true);
    }
  }, [
    accessToken,
    setUser,
    storeSetCurrentOrg,
    storeSetOrganizations,
    storeLogout,
  ]);

  useEffect(() => {
    if (!hasInitialized && hasHydrated) {
      refreshUser();
    }
  }, [hasInitialized, hasHydrated, refreshUser]);

  // ── Session heartbeat ──────────────────────────────────────────────
  // Periodically validate the session so we detect server-side expiry
  // even if the user isn't actively clicking. Pauses when the tab is
  // hidden and resumes + checks immediately when the tab becomes visible.
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Only run heartbeat when we think we're authenticated
    if (!accessToken || !hasInitialized) return;

    // Snapshot the token this effect was started with. If the token
    // changes (e.g. a new OTP login), the effect re-runs and the old
    // closure's check() becomes a no-op — preventing stale heartbeat
    // responses from triggering logout on the new session.
    const effectToken = accessToken;

    const check = async () => {
      // If token has already changed since this check was scheduled, skip
      if (useAuthStore.getState().accessToken !== effectToken) return;
      try {
        await api.getCurrentUser();
      } catch (error) {
        // SessionExpiredError means apiFetch already called logout()
        // and cleared the store — React will re-render and ProtectedRoute
        // will redirect to /login.
        if (error instanceof SessionExpiredError) {
          console.warn("Session expired (heartbeat). Redirecting to login.");
        }
      }
    };

    const startHeartbeat = () => {
      stopHeartbeat();
      heartbeatRef.current = setInterval(check, HEARTBEAT_INTERVAL_MS);
    };

    const stopHeartbeat = () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };

    // Pause when tab is hidden, resume + check when visible
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        // User came back — check immediately, then resume interval
        check();
        startHeartbeat();
      } else {
        stopHeartbeat();
      }
    };

    startHeartbeat();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stopHeartbeat();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [accessToken, hasInitialized]);

  // ── Sync external logouts ──────────────────────────────────────────
  // When apiFetch calls store.logout() outside of React (e.g. from a
  // failed refresh), the zustand reactive hook triggers a re-render so
  // ProtectedRoute sees isAuthenticated === false.  We just need to
  // ensure isLoading is false so the UI shows the unauthenticated state
  // immediately (not a loading spinner).
  //
  // IMPORTANT: Do NOT reset hasInitialized here.  Doing so would re-
  // trigger refreshUser when fresh tokens arrive from a concurrent OTP
  // login, creating a race condition where stale token failures kill the
  // new session.
  useEffect(() => {
    const unsub = useAuthStore.subscribe((state, prev) => {
      if (prev.accessToken && !state.accessToken) {
        setIsLoading(false);
      }
    });
    return unsub;
  }, []);

  // Login with OTP
  const login = useCallback(
    async (email: string, code: string) => {
      const result = await api.verifyOtp(email, code);

      // Set tokens first so getCurrentUser can authenticate
      setTokens(result.accessToken, result.refreshToken);

      // Fetch full user with organizations BEFORE setting user
      // This ensures organizations are in store before isAuthenticated becomes true
      const userData = await api.getCurrentUser();
      storeSetOrganizations(userData.organizations || []);

      if (userData.organizations?.length > 0) {
        storeSetCurrentOrg(userData.organizations[0] ?? null);
      }

      // Set user LAST - this triggers isAuthenticated to become true
      // At this point, organizations are already set in the store
      setUser(userData);
    },
    [setTokens, setUser, storeSetCurrentOrg, storeSetOrganizations],
  );

  // Logout
  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Ignore errors during logout
    }
    storeLogout();
    // storeLogout already clears organizations in the store
  }, [storeLogout]);

  // Organization switching
  const setCurrentOrganization = useCallback(
    (org: Organization | null) => {
      const prevOrg = useAuthStore.getState().currentOrganization;
      storeSetCurrentOrg(org);
      // Clear brand when switching to a different org (not just refreshing)
      if (!prevOrg || !org || prevOrg.id !== org.id) {
        storeSetCurrentBrand(null);
        storeSetBrands([]);
      }
    },
    [storeSetCurrentOrg, storeSetCurrentBrand, storeSetBrands],
  );

  // Brand switching
  const setCurrentBrand = useCallback(
    (brand: Brand | null) => {
      storeSetCurrentBrand(brand);
    },
    [storeSetCurrentBrand],
  );

  // Brand list management
  const setBrands = useCallback(
    (brands: Brand[]) => {
      storeSetBrands(brands);
    },
    [storeSetBrands],
  );

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user && !!accessToken,
    organizations,
    currentOrganization,
    currentBrand,
    brands,
    login,
    logout,
    setCurrentOrganization,
    setCurrentBrand,
    setBrands,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
